/**
 * Reasoning Prompts — hardcoded config-key manifest + small shared helpers.
 *
 * The 6 keys below are the ones the quill-engine ACTUALLY reads from
 * quill_guardrail_reasoning_config (verified against engine source + live DB
 * rows, 2026-07-24, matching quill-admin-backend's VALID_CONFIG_KEYS). All 6
 * have live rows today, but GET /config returns active rows only — a key
 * absent from that response means "not configured yet" (no rows at all), not
 * an error, and saving to it creates version 1.
 */

import { QuillApiError, ReasoningConfigKey, ReasoningConfigRow } from '../../../services/quillAdminService';

/**
 * How the engine treats one of the four editable fields for one key.
 *
 *   'read'        the engine reads this column from the config row and uses it.
 *   'superseded'  the engine reads a DIFFERENT source and this column is at
 *                 best a fallback. `supersededBy` names the real one.
 *   'ignored'     nothing reads it for this key. Editing it does nothing.
 */
export type FieldSupport =
  | { support: 'read' }
  | { support: 'superseded'; supersededBy: string }
  | { support: 'ignored'; note: string };

export interface ReasoningFieldMap {
  model: FieldSupport;
  temperature: FieldSupport;
  max_tokens: FieldSupport;
}

export interface ReasoningConfigManifestEntry {
  key: ReasoningConfigKey;
  displayName: string;
  description: string;
  /** Which of model / temperature / max_tokens the engine actually reads for THIS key. */
  fields: ReasoningFieldMap;
}

const READ: FieldSupport = { support: 'read' };
const IGNORED_NOT_SELECTED: FieldSupport = {
  support: 'ignored',
  note: 'The engine selects only prompt_body for this key.',
};

// ── PER-KEY FIELD SUPPORT (2026-08-03 admin wiring audit, finding F9) ────────
//
// The editor showed the same four inputs for every key. It is four inputs on
// exactly ZERO of the six: prompt_body is the only field every key reads, and
// which of the other three do anything differs per key — one of them
// (`model`) is overridden by a pipeline row on three keys, so editing it here
// looks like it sets the model and does not.
//
// Derived by reading the engine's call sites, not by reading a table:
//
//   constitutional_classifier_prompt   guardrails/classifier/llmClassifier.js
//     prompt_body ✓, temperature ✓ (:171), max_tokens ✓ (:172)
//     model → the `guardrails_classifier` PIPELINE ROW (:158); the column is a
//     last-resort fallback and the engine's own SQL calls it DEPRECATED.
//
//   refusal_generation_prompt          guardrails/refusal/renderer.js
//     prompt_body ✓, temperature ✓, max_tokens ✓
//     model → the `refusal_generation` pipeline row; fallback only.
//
//   fuzzy_interpretation_prompt        services/fuzzyInterpretationService.js
//     SELECTs prompt_body ALONE. model/temperature/max_tokens are never read.
//     (The model comes from the `fuzzy_interpretation` pipeline row.)
//
//   story_completion_classifier_prompt prompts/storyCompletionClassifier.js
//     SELECTs prompt_body ALONE — same as above.
//
//   interview_coverage_judge_prompt    prompts/interviewCoverageJudge.js +
//                                      services/interviewCoverageService.js
//     prompt_body ✓.
//     model → the `interview_coverage_judge` pipeline row governs; this column
//       applies ONLY when the resolver produces no model at all.
//     temperature → applies ONLY when the pipeline row's temperature is NULL
//       (interviewCoverageService: `if (judge.temperature == null)`).
//     max_tokens → never read.
//
//   mirror_listener_prompt             services/mirrorListener.js
//     prompt_body ✓, temperature ✓.
//     model → loadPromptConfig selects it, but llmAssist destructures only
//       {promptBody, temperature}; the model comes from the `mirror_listener`
//       pipeline row.
//     max_tokens → a module constant (DEFAULT_MAX_TOKENS), never this column.
//
// The editor hides an 'ignored' field and replaces a 'superseded' one with a
// one-line pointer. The BACKEND still accepts all four — history and restore
// round-trip the whole row, and narrowing the write would break rollback.
export const REASONING_CONFIG_MANIFEST: ReasoningConfigManifestEntry[] = [
  {
    key: 'constitutional_classifier_prompt',
    displayName: 'Constitutional Classifier',
    description: 'Classifies every member turn on/off-mission (the guardrail brain)',
    fields: {
      model: { support: 'superseded', supersededBy: 'the guardrails_classifier pipeline row' },
      temperature: READ,
      max_tokens: READ,
    },
  },
  {
    key: 'refusal_generation_prompt',
    displayName: 'Refusal Generation',
    description: 'Generates the warm refusal wording members see',
    fields: {
      model: { support: 'superseded', supersededBy: 'the refusal_generation pipeline row' },
      temperature: READ,
      max_tokens: READ,
    },
  },
  {
    key: 'fuzzy_interpretation_prompt',
    displayName: 'Fuzzy Interpretation',
    description: 'Interprets vague or ambiguous member requests into concrete intents',
    fields: {
      model: { support: 'superseded', supersededBy: 'the fuzzy_interpretation pipeline row' },
      temperature: IGNORED_NOT_SELECTED,
      max_tokens: IGNORED_NOT_SELECTED,
    },
  },
  {
    key: 'story_completion_classifier_prompt',
    displayName: 'Story Completion Classifier',
    description: 'Detects when a member has finished telling a story (drives save offers)',
    fields: {
      model: { support: 'superseded', supersededBy: 'the story_completion pipeline row' },
      temperature: IGNORED_NOT_SELECTED,
      max_tokens: IGNORED_NOT_SELECTED,
    },
  },
  {
    key: 'interview_coverage_judge_prompt',
    displayName: 'Interview Coverage Judge',
    description: "Judges how completely a member's stories answer interview questions",
    fields: {
      model: {
        support: 'superseded',
        supersededBy: 'the interview_coverage_judge pipeline row (this is used only if that row resolves no model at all)',
      },
      temperature: {
        support: 'superseded',
        supersededBy: "the interview_coverage_judge pipeline row (this applies only when that row's temperature is empty)",
      },
      max_tokens: { support: 'ignored', note: 'The coverage judge does not send a token cap.' },
    },
  },
  {
    key: 'mirror_listener_prompt',
    displayName: 'Mirror Listener Prompt',
    description: "The Mirror's listener-stage prompt",
    fields: {
      model: { support: 'superseded', supersededBy: 'the mirror_listener pipeline row' },
      temperature: READ,
      max_tokens: { support: 'ignored', note: 'The listener uses a fixed token budget in code.' },
    },
  },
];

/** The field map for a key; falls back to "everything is read" for an unknown key. */
export function fieldsForKey(key: ReasoningConfigKey): ReasoningFieldMap {
  const entry = REASONING_CONFIG_MANIFEST.find((e) => e.key === key);
  return entry ? entry.fields : { model: READ, temperature: READ, max_tokens: READ };
}

export const DEFAULT_MODEL = 'grok-3-mini';
export const DEFAULT_TEMPERATURE = 0;
export const DEFAULT_MAX_TOKENS = 300;

/** Editable buffer for the PromptEditor — mirrors UpdateReasoningConfigRequest plus a required edit_note. */
export interface ReasoningEditorBuffer {
  prompt_body: string;
  model: string;
  temperature: number;
  max_tokens: number;
  edit_note: string;
}

/** Builds the editor buffer for a key: from its active row if configured, or defaults (v1) if not. */
export function bufferFromRow(row: ReasoningConfigRow | null): ReasoningEditorBuffer {
  if (!row) {
    return {
      prompt_body: '',
      model: DEFAULT_MODEL,
      temperature: DEFAULT_TEMPERATURE,
      max_tokens: DEFAULT_MAX_TOKENS,
      edit_note: '',
    };
  }
  return {
    prompt_body: row.prompt_body,
    model: row.model,
    temperature: row.temperature,
    max_tokens: row.max_tokens,
    edit_note: '',
  };
}

/** Dirty against the loaded baseline — edit_note is intentionally excluded (it's write-only, not a field being edited). */
export function isBufferDirty(buffer: ReasoningEditorBuffer, baseline: ReasoningEditorBuffer): boolean {
  return (
    buffer.prompt_body !== baseline.prompt_body ||
    buffer.model !== baseline.model ||
    buffer.temperature !== baseline.temperature ||
    buffer.max_tokens !== baseline.max_tokens
  );
}

export function describeReasoningError(e: unknown): string {
  if (e instanceof QuillApiError) {
    return e.message || `Request failed (${e.status})`;
  }
  return e instanceof Error ? e.message : 'Unexpected error';
}
